import { useState } from 'react';
import type { VendorTenant, TenantTier, OnPremTier } from '@/lib/vendorApi';
import type { Action } from './TenantTable';
import { vendorTenantsApi } from '@/lib/vendorApi';

const ONPREM_TIER_LABELS: Record<OnPremTier, string> = {
  starter:    'Starter (3 users)',
  growth:     'Growth (15 users)',
  enterprise: 'Enterprise (unlimited)',
};

interface Props {
  tenant:   VendorTenant | null;
  action:   Action | null;
  onClose:  () => void;
  onDone:   () => void;
}

export function TenantDrawer({ tenant, action, onClose, onDone }: Props) {
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [result,       setResult]       = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [expiresAt,    setExpiresAt]    = useState('');
  const [confirmName,  setConfirmName]  = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [tierChoice,   setTierChoice]   = useState<TenantTier>(tenant?.tier ?? 'standard');
  // Generate-key tier (on-prem tiers). Default Enterprise — the safe pick
  // when an admin clicks Generate Key without thinking about user caps.
  const [onPremTier,   setOnPremTier]   = useState<OnPremTier>('enterprise');

  if (!tenant || !action) return null;

  async function execute() {
    if (!tenant || !action) return;
    setLoading(true);
    setError('');
    try {
      if (action === 'generate-key') {
        const res = await vendorTenantsApi.generateKey(tenant.id, billingEmail || undefined, onPremTier);
        setGeneratedKey(res.key);
        const tierNote = res.max_users === null ? 'unlimited users' : `${res.max_users} users`;
        setResult(`License key generated for ${ONPREM_TIER_LABELS[res.tier]} (${tierNote}). Expires ${new Date(res.expires_at).toLocaleDateString()}.`);
        onDone();
      } else if (action === 'extend') {
        await vendorTenantsApi.extend(tenant.id, new Date(expiresAt).toISOString());
        setResult('Expiry date updated.');
        onDone();
      } else if (action === 'reset-demo') {
        await vendorTenantsApi.resetDemo(tenant.id);
        setResult('Demo period reset to 30 days.');
        onDone();
      } else if (action === 'revoke') {
        await vendorTenantsApi.revoke(tenant.id, confirmName);
        setResult('License revoked.');
        onDone();
      } else if (action === 'set-tier') {
        const res = await vendorTenantsApi.setTier(tenant.id, tierChoice);
        setResult(`Tier set to ${res.tier === 'pro' ? 'Pro' : 'Standard'}.`);
        onDone();
      } else if (action === 'hard-delete') {
        const res = await vendorTenantsApi.hardDelete(tenant.id, confirmName);
        const blobNote = res.blobs_failed > 0 ? ` (${res.blobs_failed} attachment file${res.blobs_failed === 1 ? '' : 's'} could not be removed from storage — orphaned)` : '';
        setResult(`Tenant permanently deleted. Removed ${res.attachments_deleted} attachment${res.attachments_deleted === 1 ? '' : 's'}.${blobNote}`);
        onDone();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  const ACTION_LABELS: Record<Action, string> = {
    'generate-key': 'Generate License Key',
    'extend':       'Extend Expiry',
    'reset-demo':   'Reset Demo',
    'revoke':       'Revoke License',
    'set-tier':     'Change Feature Tier',
    'hard-delete':  'Delete tenant permanently',
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-20" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-96 bg-slate-800 border-l border-slate-700 z-30 flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{ACTION_LABELS[action]}</p>
            <p className="text-slate-100 font-semibold mt-0.5">{tenant.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">

          {/* Generate Key */}
          {action === 'generate-key' && !generatedKey && (
            <>
              <p className="text-slate-400 text-sm">
                Generates a new 1-year RS256 license key for <strong className="text-slate-200">{tenant.name}</strong> and emails it to the billing contact.
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Billing Email {tenant.billing_email ? `(current: ${tenant.billing_email})` : '(required — not set)'}
                </label>
                <input
                  type="email"
                  value={billingEmail}
                  onChange={e => setBillingEmail(e.target.value)}
                  placeholder={tenant.billing_email ?? 'billing@customer.com'}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Tier
                </label>
                <select
                  value={onPremTier}
                  onChange={e => setOnPremTier(e.target.value as OnPremTier)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="starter">{ONPREM_TIER_LABELS.starter}</option>
                  <option value="growth">{ONPREM_TIER_LABELS.growth}</option>
                  <option value="enterprise">{ONPREM_TIER_LABELS.enterprise}</option>
                </select>
                <p className="text-slate-500 text-xs mt-1">
                  Caps the number of active users on the customer's on-prem install. Encoded in the signed license key.
                </p>
              </div>
            </>
          )}

          {/* Generated key result */}
          {action === 'generate-key' && generatedKey && (
            <>
              <p className="text-emerald-400 text-sm font-medium">{result}</p>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">License Key</label>
                <textarea
                  readOnly
                  value={generatedKey}
                  rows={6}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-emerald-400 text-xs font-mono resize-none"
                />
                <button
                  onClick={() => navigator.clipboard.writeText(generatedKey)}
                  className="mt-2 text-xs text-brand-400 hover:text-brand-300 underline"
                >
                  Copy to clipboard
                </button>
              </div>
            </>
          )}

          {/* Extend */}
          {action === 'extend' && !result && (
            <>
              <p className="text-slate-400 text-sm">Set a new expiry date for <strong className="text-slate-200">{tenant.name}</strong>.</p>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">New expiry date</label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={e => setExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </>
          )}

          {/* Reset Demo */}
          {action === 'reset-demo' && !result && (
            <p className="text-slate-400 text-sm">
              Reset the demo timer for <strong className="text-slate-200">{tenant.name}</strong> to 30 days from today. This will clear any existing license key.
            </p>
          )}

          {/* Revoke */}
          {action === 'revoke' && !result && (
            <>
              <p className="text-red-400 text-sm font-medium">This will immediately expire the license. The tenant's next API call will return 402.</p>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Type <strong className="text-slate-200">{tenant.name}</strong> to confirm
                </label>
                <input
                  type="text"
                  value={confirmName}
                  onChange={e => setConfirmName(e.target.value)}
                  placeholder={tenant.name}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </>
          )}

          {/* Hard Delete — irreversible, requires already-revoked tenant. */}
          {action === 'hard-delete' && !result && (
            <>
              <p className="text-red-500 text-sm font-semibold">
                This permanently removes <strong className="text-slate-100">{tenant.name}</strong>, every user, work order, asset, and uploaded file. There is no undo.
              </p>
              {tenant.revoked_at && (
                <p className="text-slate-500 text-xs">
                  Revoked {Math.floor((Date.now() - new Date(tenant.revoked_at).getTime()) / 86400_000)} day(s) ago.
                </p>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Type <strong className="text-slate-200">{tenant.name}</strong> to confirm
                </label>
                <input
                  type="text"
                  value={confirmName}
                  onChange={e => setConfirmName(e.target.value)}
                  placeholder={tenant.name}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </>
          )}

          {/* Set Tier */}
          {action === 'set-tier' && !result && (
            <>
              <p className="text-slate-400 text-sm">
                Set the SaaS feature tier for <strong className="text-slate-200">{tenant.name}</strong>.
                {tenant.plan === 'onprem' && (
                  <span className="block mt-2 text-amber-400 text-xs">
                    Note: this tenant is on-prem, which always gets all features regardless of tier.
                  </span>
                )}
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Current tier</label>
                <p className="text-slate-200 text-sm mb-3">{tenant.tier === 'pro' ? 'Pro' : 'Standard'}</p>
                <label className="block text-xs font-medium text-slate-400 mb-1">New tier</label>
                <select
                  value={tierChoice}
                  onChange={e => setTierChoice(e.target.value as TenantTier)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="standard">Standard — Core CMMS</option>
                  <option value="pro">Pro — adds meter-based PMs, KPI reports</option>
                </select>
              </div>
            </>
          )}

          {/* Success (non-generate-key) */}
          {result && action !== 'generate-key' && (
            <p className="text-emerald-400 text-sm font-medium">{result}</p>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}
        </div>

        {/* Footer buttons */}
        <div className="px-6 py-4 border-t border-slate-700 flex gap-3">
          {!result && (
            <button
              onClick={execute}
              disabled={
                loading ||
                (action === 'extend'       && !expiresAt) ||
                (action === 'revoke'       && confirmName !== tenant.name) ||
                (action === 'hard-delete'  && confirmName !== tenant.name) ||
                (action === 'generate-key' && !tenant.billing_email && !billingEmail) ||
                (action === 'set-tier'     && tierChoice === tenant.tier)
              }
              className={`flex-1 font-semibold py-2 px-4 rounded-lg text-sm transition-colors disabled:opacity-40 ${
                action === 'revoke' || action === 'hard-delete'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-brand-600 hover:bg-brand-700 text-white'
              }`}
            >
              {loading ? 'Working...' : ACTION_LABELS[action]}
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-200 font-semibold py-2 px-4 rounded-lg text-sm transition-colors"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </>
  );
}
